import { useEffect, useState } from 'react';
import apiService from '../services/api';
import { Boundary } from '../utils/boundary';
import * as fmt from '../lib/format';

// Where "Before <month>" starts, read once and kept for a minute (an admin may move it in Settings → Standards).
// Not signed in, or the read fails: null, and the pickers and pages simply show no divider and no notice.
const KEEP_MS = 60_000;
let cached: { at: number; value: Boundary } | null = null;
let inflight: Promise<Boundary | null> | null = null;

function load(): Promise<Boundary | null> {
  if (cached && Date.now() - cached.at < KEEP_MS) return Promise.resolve(cached.value);
  if (!inflight) {
    inflight = (async () => {
      try {
        const body = await apiService.getBoundaries();
        const d = body?.data;
        if (!body?.success || !d?.tech_from) return cached?.value ?? null;
        cached = { at: Date.now(), value: { day: d.tech_from, month: d.tech_from_month, quarter: d.tech_from_quarter } };
        return cached.value;
      } catch {
        return cached?.value ?? null;
      } finally {
        inflight = null;
      }
    })();
  }
  return inflight;
}

export interface Boundaries extends Boundary {
  /** "Before July 2026" */
  label: string;
  /** "From July 2026" */
  fromLabel: string;
}

export function useBoundaries(): Boundaries | null {
  const [value, setValue] = useState<Boundary | null>(cached?.value ?? null);
  useEffect(() => {
    let live = true;
    load().then((b) => {
      if (live && b) setValue(b);
    });
    return () => {
      live = false;
    };
  }, []);
  return value ? { ...value, label: `Before ${fmt.month(value.month)}`, fromLabel: `From ${fmt.month(value.month)}` } : null;
}
